import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Spinner } from '../components/Spinner';

/* ── Shared layout wrapper ── */
function AuthShell({ children }) {
  return (
    <div className="min-h-screen flex">
      {/* Left panel — navy brand strip (desktop only) */}
      <div className="hidden md:flex flex-col justify-between w-[400px] flex-shrink-0 bg-navy px-12 py-14">
        <div className="flex flex-col items-center text-center">
          <img src="/MST Logo White.png" alt="MST Sports Tours" style={{ height: 72, display: 'block' }} className="mb-8" />
          <h2 className="text-[26px] font-extrabold text-white leading-[1.2] tracking-[-0.5px] mb-3">
            MST Sports Tour Portal
          </h2>
          <p className="text-[14px] text-white/60 leading-relaxed">
            For staff, suppliers and customers
          </p>
        </div>
        <div>
          <p className="text-[11px] text-white/30 text-center">© {new Date().getFullYear()} MST Sports Tours</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center bg-page px-4 py-12">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="md:hidden flex justify-center mb-8">
            <div className="w-[44px] h-[44px] rounded-[10px] bg-navy flex items-center justify-center">
              <img src="/MST Mark White.png" alt="MST" style={{ height: 26 }} />
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">
      {children}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className="w-full border border-[#E5E7EB] rounded-[9px] px-4 py-[11px] text-[16px] focus:outline-none focus:ring-2 focus:ring-pb focus:border-pb bg-white"
    />
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPortalPicker, setShowPortalPicker] = useState(false);
  const [pickerRoles, setPickerRoles] = useState([]);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const savedPortal = (() => { try { return localStorage.getItem('mst_preferred_portal'); } catch { return null; } })();

  function navigateByRoles(roles, preferPortal) {
    // If redirected here from a protected page, go back to that page after login
    const intendedPath = location.state?.from?.pathname
      ? `${location.state.from.pathname}${location.state.from.hash || ''}${location.state.from.search || ''}`
      : null;

    const hasOps = roles.some((r) => r === 'admin' || r === 'staff');
    const hasTourLeader = roles.includes('tour_leader');
    if (preferPortal === 'ops' || (hasOps && !hasTourLeader)) {
      try { localStorage.setItem('mst_preferred_portal', 'ops'); } catch {}
      return navigate(intendedPath && intendedPath.startsWith('/staff') ? intendedPath : '/staff');
    }
    if (preferPortal === 'tour_leader' || (hasTourLeader && !hasOps)) {
      try { localStorage.setItem('mst_preferred_portal', 'tour_leader'); } catch {}
      return navigate(intendedPath && intendedPath.startsWith('/tour-leader') ? intendedPath : '/tour-leader');
    }
    if (roles.includes('football_ops')) return navigate(intendedPath && intendedPath.startsWith('/football-ops') ? intendedPath : '/football-ops');
    if (roles.includes('customer')) return navigate(intendedPath && intendedPath.startsWith('/customer') ? intendedPath : '/customer');
    if (roles.includes('supplier')) return navigate(intendedPath && intendedPath.startsWith('/supplier') ? intendedPath : '/supplier');
    if (roles.includes('reseller')) return navigate(intendedPath && intendedPath.startsWith('/reseller') ? intendedPath : '/reseller');
    if (roles.includes('marketing')) return navigate(intendedPath && intendedPath.startsWith('/marketing') ? intendedPath : '/marketing');
    navigate('/login');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      const docSnap = await getDoc(doc(db, 'users', result.user.uid));
      if (!docSnap.exists()) {
        setError('User profile not found. Please contact MST.');
        setLoading(false);
        return;
      }
      const data = docSnap.data();
      const roles = Array.isArray(data.roles) ? data.roles : data.role ? [data.role] : [];
      if (roles.length === 0) {
        setError('No portal access configured. Please contact MST.');
        setLoading(false);
        return;
      }
      const hasOps = roles.some((r) => r === 'admin' || r === 'staff');
      const hasTourLeader = roles.includes('tour_leader');
      if (hasOps && hasTourLeader) {
        setPickerRoles(roles);
        setShowPortalPicker(true);
        setLoading(false);
        return;
      }
      navigateByRoles(roles);
    } catch {
      setError('Incorrect email or password.');
    }
    setLoading(false);
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setForgotError('');
    if (!forgotEmail.trim()) { setForgotError('Please enter your email address.'); return; }
    setForgotLoading(true);
    try {
      await addDoc(collection(db, 'passwordResetRequests'), {
        email: forgotEmail.trim().toLowerCase(),
        requestedAt: serverTimestamp(),
      });
      setForgotSent(true);
    } catch {
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  }

  /* ── Portal picker ── */
  if (showPortalPicker) {
    return (
      <AuthShell>
        <div className="text-center mb-8">
          <h1 className="text-[24px] font-extrabold text-navy tracking-[-0.4px]">Choose your portal</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">You have access to both. Choose one for this session.</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { key: 'ops', label: 'Ops Portal', sub: 'Tour building, bookings, finance', icon: (
              <svg className="w-6 h-6 text-pb" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3" />
              </svg>
            ), iconBg: 'bg-blue-light', active: savedPortal === 'ops', activeBorder: 'border-pb', activeTag: 'bg-pb' },
            { key: 'tour_leader', label: 'Tour Leader', sub: 'On-tour itinerary & registers', icon: (
              <svg className="w-6 h-6 text-[#C2410C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
            ), iconBg: 'bg-[#FFF7ED]', active: savedPortal === 'tour_leader', activeBorder: 'border-[#C2410C]', activeTag: 'bg-[#C2410C]' },
          ].map(({ key, label, sub, icon, iconBg, active, activeBorder, activeTag }) => (
            <button key={key} onClick={() => navigateByRoles(pickerRoles, key)}
              className={`relative flex flex-col items-center text-center border-2 rounded-[14px] px-4 py-6 transition-all hover:shadow-md ${
                active ? `${activeBorder} bg-white` : 'border-[#E5E7EB] hover:border-[#9CA3AF] bg-white'
              }`}>
              {active && <span className={`absolute top-3 right-3 text-[10px] font-bold px-[7px] py-[2px] rounded-[6px] text-white ${activeTag}`}>Last used</span>}
              <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center mb-3`}>{icon}</div>
              <p className="text-[14px] font-bold text-[#111827] mb-1">{label}</p>
              <p className="text-[11.5px] text-[#6B7280] leading-snug">{sub}</p>
            </button>
          ))}
        </div>
        <button onClick={() => { setShowPortalPicker(false); setPickerRoles([]); }}
          className="w-full text-[13px] font-semibold text-[#9CA3AF] hover:text-[#6B7280] transition py-2">
          ← Back to sign in
        </button>
      </AuthShell>
    );
  }

  /* ── Forgot password ── */
  if (showForgot) {
    return (
      <AuthShell>
        <div className="mb-8">
          <h1 className="text-[24px] font-extrabold text-navy tracking-[-0.4px]">Reset your password</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">We'll send you a link to set a new one.</p>
        </div>
        {forgotSent ? (
          <div className="space-y-5">
            <div className="bg-green-light border border-sg/25 text-sg rounded-[9px] px-4 py-4 text-[13px]">
              If an account exists for <strong>{forgotEmail}</strong>, a reset link has been sent. Check your inbox.
            </div>
            <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
              className="w-full text-[13px] font-bold text-pb hover:text-navy transition py-2">
              ← Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <FieldLabel>Email address</FieldLabel>
              <TextInput type="email" autoFocus value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            {forgotError && <p className="text-[13px] text-dr">{forgotError}</p>}
            <button type="submit" disabled={forgotLoading}
              className="w-full bg-pb hover:bg-navy text-white font-bold py-[11px] rounded-[9px] text-[14px] transition disabled:opacity-50 flex items-center justify-center gap-2">
              {forgotLoading && <Spinner size={15} />}
              {forgotLoading ? 'Sending…' : 'Send reset link'}
            </button>
            <button type="button" onClick={() => { setShowForgot(false); setForgotError(''); }}
              className="w-full text-[13px] font-semibold text-[#9CA3AF] hover:text-[#6B7280] transition py-1">
              ← Back to sign in
            </button>
          </form>
        )}
      </AuthShell>
    );
  }

  /* ── Sign in ── */
  return (
    <AuthShell>
      <div className="mb-8">
        <h1 className="text-[24px] font-extrabold text-navy tracking-[-0.4px]">Welcome back</h1>
        <p className="text-[13px] text-[#6B7280] mt-1">Sign in to your MST Sports Tour Portal.</p>
      </div>

      {error && (
        <div className="bg-red-light border border-dr/25 text-dr rounded-[9px] px-4 py-3 mb-5 text-[13px] font-semibold">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <FieldLabel>Email address</FieldLabel>
          <TextInput type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-[6px]">
            <FieldLabel>Password</FieldLabel>
            <button type="button"
              onClick={() => { setShowForgot(true); setForgotEmail(email); setError(''); }}
              className="text-[11.5px] font-bold text-pb hover:text-navy transition">
              Forgot password?
            </button>
          </div>
          <TextInput type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
        </div>

        <button type="submit" disabled={loading}
          className="w-full bg-pb hover:bg-navy text-white font-bold py-[11px] rounded-[9px] text-[14px] transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
          {loading && <Spinner size={15} />}
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="text-center text-[11.5px] text-[#9CA3AF] mt-8 leading-relaxed">
        Access is by invitation only.{' '}
        <span className="text-[#6B7280]">Contact MST to request an account.</span>
      </p>
    </AuthShell>
  );
}
